export const GROUPED_VARIANT_TYPES = ['group_by_variant', 'group_by_attributes'] as const

export type GroupedVariantType = typeof GROUPED_VARIANT_TYPES[number]

export const isGroupedVariantType = (variantType: string | null | undefined): boolean =>
  variantType != null && GROUPED_VARIANT_TYPES.some(groupedType => groupedType === variantType)

type VariantInfo = { numVariants: number | null, variants: readonly unknown[] }

// Whether the variants list was loaded, i.e. the request asked for the `variants` feature. Today that's
// only the PDP's own product detail request; PLP search results and PDP product sets (alternatives,
// related, recently viewed) don't request it, so this is false there even for products with variants.
// Use this only when you need the variants themselves, e.g. the Variants widget's swatches.
export const hasVariantList = (product: VariantInfo): boolean =>
  product.variants.length > 0

// Whether the product has variants at all. `num_variants` is in every product response (PLP search
// results, PDP product sets, PDP product detail); the list is the fallback for products without a
// count. Use this for "does this product have variants" decisions, e.g. Variant Indication and the
// has_variants condition.
export const hasVariants = (product: VariantInfo): boolean =>
  (product.numVariants ?? 0) > 0 || hasVariantList(product)

// Whether the product is part of a variant group: its variant type groups variants and it has siblings.
// An unknown count (`null`) counts as having siblings. Backs the variant_grouping condition and the
// Order Form's "Choose your variant" button.
export const isPartOfVariantGroup = (numVariants: number | null | undefined, variantType: string | null | undefined): boolean =>
  isGroupedVariantType(variantType) && (numVariants == null || numVariants > 0)
